<template>
  <IonPage>
    <IonContent class="storyblok-content">
      <div class="storyblok-preview-toolbar">
        <IonButton
          class="storyblok-theme-toggle"
          :class="previewThemeMode === 'modern' ? 'theme-selected' : ''"
          :fill="previewThemeMode === 'modern' ? 'solid' : 'outline'"
          size="small"
          aria-label="Use modern theme"
          @click="setPreviewTheme('modern')"
        >
          Modern
        </IonButton>
        <IonButton
          class="storyblok-theme-toggle"
          :class="previewThemeMode === 'classic' ? 'theme-selected' : ''"
          :fill="previewThemeMode === 'classic' ? 'solid' : 'outline'"
          size="small"
          aria-label="Use classic theme"
          @click="setPreviewTheme('classic')"
        >
          Classic
        </IonButton>
        <IonButton
          class="storyblok-theme-toggle"
          :class="previewThemeMode === 'dark' ? 'theme-selected' : ''"
          :fill="previewThemeMode === 'dark' ? 'solid' : 'outline'"
          size="small"
          aria-label="Use dark theme"
          @click="setPreviewTheme('dark')"
        >
          Dark
        </IonButton>
      </div>
      <div v-if="error" class="storyblok-preview-error">{{ error }}</div>
      <StoryblokComponent
        v-editable="story"
        v-else-if="story"
        :blok="story.content"
        class="storyblok-component"
      />
    </IonContent>
  </IonPage>
</template>

<script setup lang="ts">
import { IonButton, IonContent, IonPage } from '@ionic/vue';
import type { ISbStoryData, SbBlokData } from '@storyblok/vue';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStoryblokApi, useStoryblokBridge } from '@storyblok/vue';
import { SUPPORTED_LOCALES } from '@/i18n';

type PreviewThemeMode = 'modern' | 'classic' | 'dark';

const route = useRoute();
const story = ref<ISbStoryData<SbBlokData> | null>(null);
const error = ref<string | null>(null);
const bridgeStoryId = ref<number | null>(null);
const previewThemeMode = ref<PreviewThemeMode>('modern');
const supportedLocaleCodes = new Set(SUPPORTED_LOCALES.map(({ code }) => code.toLowerCase()));
const initialBodyThemeClasses = {
  dark: document.body.classList.contains('dark'),
  darkTheme: document.body.classList.contains('dark-theme'),
  classicTheme: document.body.classList.contains('classic-theme'),
};

if (initialBodyThemeClasses.classicTheme) {
  previewThemeMode.value = 'classic';
} else if (initialBodyThemeClasses.dark || initialBodyThemeClasses.darkTheme) {
  previewThemeMode.value = 'dark';
}

const slug = computed(() => {
  const rawSlug = String(route.query.slug || 'home').trim();
  return rawSlug.replace(/^\/+|\/+$/g, '') || 'home';
});

const storyRequest = computed(() => {
  const slugSegments = slug.value.split('/').filter(Boolean);
  const localePrefix = slugSegments[0]?.toLowerCase();

  if (localePrefix && supportedLocaleCodes.has(localePrefix) && slugSegments.length > 1) {
    return {
      slug: slugSegments.slice(1).join('/'),
      language: localePrefix,
    };
  }

  return {
    slug: slug.value,
    language: undefined,
  };
});

const setupBridge = (storyId: number): void => {
  if (bridgeStoryId.value === storyId) {
    return;
  }

  useStoryblokBridge(storyId, (newStory) => {
    story.value = newStory;
    error.value = null;
  });
  bridgeStoryId.value = storyId;
};

const applyPreviewTheme = (mode: PreviewThemeMode): void => {
  const shouldBeDark = mode === 'dark' || mode === 'classic';

  document.body.classList.toggle('dark', shouldBeDark);
  document.body.classList.remove('dark-theme', 'classic-theme');

  if (mode === 'classic') {
    document.body.classList.add('classic-theme');
  } else if (mode === 'dark') {
    document.body.classList.add('dark-theme');
  }
};

const setPreviewTheme = (mode: PreviewThemeMode): void => {
  previewThemeMode.value = mode;
  applyPreviewTheme(mode);
};

const fetchStory = async (): Promise<void> => {
  try {
    const api = useStoryblokApi();
    const { data } = await api.get(`cdn/stories/${storyRequest.value.slug}`, {
      version: 'draft',
      ...(storyRequest.value.language ? { language: storyRequest.value.language } : {}),
    });
    story.value = data.story;
    error.value = null;
    setupBridge(data.story.id);
  } catch (e) {
    story.value = null;
    error.value = `Story not found for slug: ${slug.value}`;
    console.error(e);
  }
};

watch(
  slug,
  () => {
    void fetchStory();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.body.classList.toggle('dark', initialBodyThemeClasses.dark);
  document.body.classList.toggle('dark-theme', initialBodyThemeClasses.darkTheme);
  document.body.classList.toggle('classic-theme', initialBodyThemeClasses.classicTheme);
});
</script>

<style scoped>
.storyblok-content {
  background: var(--ion-background-color);
  padding: var(--ion-space-6) var(--ion-space-20);
}

.storyblok-preview-toolbar {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 20;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: var(--ion-space-6);
}

.storyblok-theme-toggle {
  --border-radius: 999px;
  --padding-start: 10px;
  --padding-end: 10px;
  --box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
  --color: var(--ion-text-color);
}

.storyblok-content .storyblok-theme-toggle.theme-selected {
  --background: var(--ion-color-primary);
  --color: var(--ion-text-color-contrast);
}

.storyblok-component {
  padding: var(--ion-space-6) var(--ion-space-20);
}

.storyblok-preview-error {
  padding: 16px;
  color: var(--ion-color-danger);
}
</style>
